import React, { useContext, useEffect, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

import {
  BronzeCreditTokenLimit,
  BronzeTrialTokenLimit
} from 'app/api/MemberService';
import { getAll } from 'app/api/MembershipsService';
import UserContext from 'app/context/UserContext';

function BronzeLimit() {
  const { user } = useContext(UserContext);

  const [trialTokenLimit, setTrialTokenLimit] = useState(0);
  const [creditPurchaseToken, setCreditPurchaseToken] = useState(0);

  const [trialLoading, setTrialLoading] = useState(false);
  const [creditLoading, setCreditLoading] = useState(false);

  /* -------------------- Fetch Data -------------------- */
  const getData = async () => {
    try {
      const res = await axios.request(getAll(user.token));
      const bronze = res.data?.find(item => item.id === 1);

      if (!bronze) {
        toast.error('Bronze membership not found');
        return;
      }

      setTrialTokenLimit(bronze.trialTokenLimit ?? 0);
      setCreditPurchaseToken(bronze.creditPurchaseToken ?? 0);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load bronze limits');
    }
  };

  useEffect(() => {
    getData();
  }, [user.token]);

  /* -------------------- Validation -------------------- */
  const isValidNumber = value =>
    Number.isInteger(Number(value)) && Number(value) >= 0;

  /* -------------------- Save Handlers -------------------- */
  const handleTrialSave = async () => {
    if (!isValidNumber(trialTokenLimit)) {
      toast.error('Trial token limit must be a positive number');
      return;
    }

    try {
      setTrialLoading(true);
      await axios.request(
        BronzeTrialTokenLimit(user.token, {
          trialTokenLimit: Number(trialTokenLimit)
        })
      );

      toast.success('Trial token limit saved', { theme: 'colored' });
      getData();
    } catch (error) {
      console.error(error);
      toast.error('Failed to save trial token limit');
    } finally {
      setTrialLoading(false);
    }
  };

  const handleCreditSave = async () => {
    if (!isValidNumber(creditPurchaseToken)) {
      toast.error('Credit purchase token must be a positive number');
      return;
    }

    try {
      setCreditLoading(true);
      await axios.request(
        BronzeCreditTokenLimit(user.token, {
          creditPurchaseToken: Number(creditPurchaseToken)
        })
      );

      toast.success('Credit purchase token saved', { theme: 'colored' });
      getData();
    } catch (error) {
      console.error(error);
      toast.error('Failed to save credit purchase token');
    } finally {
      setCreditLoading(false);
    }
  };

  return (
    <Form>
      {/* Trial Token Limit */}
      <Form.Group className="mb-4">
        <Form.Label>Trial Token Limit</Form.Label>
        <Form.Control
          type="number"
          min="0"
          value={trialTokenLimit}
          onChange={e => setTrialTokenLimit(e.target.value)}
        />
        <Button
          className="mt-2"
          variant="primary"
          onClick={handleTrialSave}
          disabled={trialLoading || !isValidNumber(trialTokenLimit)}
        >
          {trialLoading ? <Spinner size="sm" /> : 'Save'}
        </Button>
      </Form.Group>

      {/* Credit Purchase Token */}
      <Form.Group className="mb-4">
        <Form.Label>Credit Purchase Token</Form.Label>
        <Form.Control
          type="number"
          min="0"
          value={creditPurchaseToken}
          onChange={e => setCreditPurchaseToken(e.target.value)}
        />
        <Button
          className="mt-2"
          variant="primary"
          onClick={handleCreditSave}
          disabled={creditLoading || !isValidNumber(creditPurchaseToken)}
        >
          {creditLoading ? <Spinner size="sm" /> : 'Save'}
        </Button>
      </Form.Group>
    </Form>
  );
}

export default BronzeLimit;
